import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React__default__default, { useRef, useState, useMemo, useLayoutEffect } from 'react';
import PropTypes__default from 'prop-types';
import styles from './dropdown.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import { useVisibilityToggler } from '../../hooks/use-visibility-toggler/use-visibility-toggler.js';
import useComponentId from '../../hooks/use-component-id/use-component-id.js';
import { InlineIconPropType } from '../../hooks/use-inline-icon/use-inline-icon.js';
import { DROPDOWN_XPOS, DROPDOWN_YPOS } from './types.js';
import { SubMenu } from '../_atoms/sub-menu/sub-menu.js';
import { SubMenuContext } from '../../contexts/sub-menu-context.js';
import { DropdownItem } from './quarks/dropdown-item.js';
import { useSubMenuInitialFocusManager } from '../../hooks/use-sub-menu-initial-focus-manager/use-sub-menu-initial-focus-manager.js';
import { Injecss } from '../../packages/injecss/injecss.js';

var bemDropdown = HoneyBem.init('dropdown', styles);
var Dropdown = function (_a) {
  var children = _a.children,
    links = _a.links,
    active = _a.active,
    _b = _a.extraClasses,
    extraClasses = _b === void 0 ? '' : _b,
    _c = _a.onToggle,
    onToggle = _c === void 0 ? function () {
      return null;
    } : _c,
    _d = _a.xPosition,
    xPosition = _d === void 0 ? 'center' : _d,
    _e = _a.yPosition,
    yPosition = _e === void 0 ? 'bottom' : _e,
    _f = _a.smartPosition,
    smartPosition = _f === void 0 ? false : _f,
    _g = _a.openOnHover,
    openOnHover = _g === void 0 ? false : _g,
    rest = __rest(_a, ["children", "links", "active", "extraClasses", "onToggle", "xPosition", "yPosition", "smartPosition", "openOnHover"]);
  // @todo: rethink this when forwardref is required
  var containerRef = useRef(null);
  var menuRef = useRef(null);
  var _h = useState(),
    lastEvent = _h[0],
    setLastEvent = _h[1];
  var handleToggle = function (isActive, event) {
    setLastEvent(event);
    if (onToggle) {
      onToggle(isActive, event);
    }
  };
  var isOutsideOfYScreenEdges = function (boundingClientRect, window) {
    return boundingClientRect.top < 0 || boundingClientRect.bottom > window.innerHeight;
  };
  var isOutsideOfXScreenEdges = function (boundingClientRect, window) {
    return boundingClientRect.left < 0 || boundingClientRect.right > window.innerWidth;
  };
  var _j = useVisibilityToggler({
      initialActive: !!active,
      onToggle: handleToggle,
      content: containerRef.current,
      hoverEvents: openOnHover,
      arrowEvents: true
    }),
    isActive = _j[0],
    targetEvents = _j[1],
    closeDropdown = _j[2];
  var focusAt = useSubMenuInitialFocusManager({
    isExpanded: isActive,
    lastEvent: lastEvent
  });
  var subMenuContextValue = useMemo(function () {
    var _a;
    return {
      setInitialFocus: focusAt,
      hidden: !isActive,
      onEscapeCallback: closeDropdown,
      target: (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild
    };
  }, [focusAt, isActive]);
  var _k = useState(yPosition),
    smartYPositionValue = _k[0],
    setSmartYPositionValue = _k[1];
  var _l = useState(xPosition),
    smartXPositionValue = _l[0],
    setSmartXPositionValue = _l[1];
  /**
   * Re-calculates dropdown yPosition value for smart positioning.
   * Runs every time an active state is triggered.
   */
  useLayoutEffect(function () {
    var _a;
    // preventing things breaking if SSR is happening
    // additionally we want smart positioning only happen is position is set to "smart"
    if (smartPosition && typeof window !== 'undefined' && typeof window.document.createElement !== 'undefined') {
      var menuPosition = (_a = menuRef === null || menuRef === void 0 ? void 0 : menuRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
      // checking if any edges of the dropdown menu going beyond the viewport
      // only then we apply "smart" positioning mechanism
      if (isOutsideOfYScreenEdges(menuPosition, window)) {
        // Checking free space above or below dropdown menu element
        var dropdownVerticalZonesSizes = [
        // top direction square
        menuPosition.top * window.innerWidth,
        // bottom direction square
        (window.innerHeight - menuPosition.bottom) * window.innerWidth];
        // finding out the max space available for vertical zones
        var maxZoneY = dropdownVerticalZonesSizes.reduce(function (accumulator, currentValue) {
          return currentValue > accumulator ? currentValue : accumulator;
        });
        // finding out the direction of that max space
        var maxZoneYDirection = DROPDOWN_YPOS[dropdownVerticalZonesSizes.indexOf(maxZoneY)];
        // showing "smart" dropdown in the direction with max space available
        setSmartYPositionValue(maxZoneYDirection);
      } else {
        // if no dropdown menu gets cut by the edges of the screen set default position
        setSmartYPositionValue(yPosition);
      }
      if (isOutsideOfXScreenEdges(menuPosition, window)) {
        // Checking free space on the sides of dropdown menu element
        var dropdownHorizontalZonesSizes = [
        // center - do nothing
        0,
        // left direction square
        menuPosition.left * window.innerHeight,
        // right direction square
        (window.innerWidth - menuPosition.right) * window.innerHeight];
        // finding out the max space available for horizontal zones
        var maxZoneX = dropdownHorizontalZonesSizes.reduce(function (accumulator, currentValue) {
          return currentValue > accumulator ? currentValue : accumulator;
        });
        // finding out the direction of that max space
        var maxZoneXDirection = DROPDOWN_XPOS[dropdownHorizontalZonesSizes.indexOf(maxZoneX)];
        // showing "smart" dropdown in the direction with max space available
        setSmartXPositionValue(maxZoneXDirection);
      } else {
        // if no dropdown menu gets cut by the edges of the screen set default position
        setSmartXPositionValue(xPosition);
      }
    }
  }, [isActive, active]);
  var handleBlur = function (event) {
    var origin = event.relatedTarget;
    if (origin && containerRef.current && containerRef.current.contains(origin)) {
      return;
    }
    closeDropdown(event);
  };
  /**
   * Generates a dynamic prefix for the component ID using the links keys or
   * index if keys are not provided
   */
  var idPrefix = links.map(function (_a, index) {
    var key = _a.key;
    return key || index.toString(16);
  }).join('');
  var dropdownListId = "dropdown-list-".concat(useComponentId(idPrefix));
  return /*#__PURE__*/React__default__default.createElement("div", __assign({
    className: bemDropdown.b({
      active: isActive,
      left: smartPosition ? smartXPositionValue === 'left' : xPosition === 'left',
      right: smartPosition ? smartXPositionValue === 'right' : xPosition === 'right',
      top: smartPosition ? smartYPositionValue === 'top' : yPosition === 'top'
    }, extraClasses),
    onBlur: handleBlur,
    ref: containerRef
  }, rest), /*#__PURE__*/React__default__default.cloneElement(children, __assign(__assign({}, targetEvents), {
    'aria-haspopup': 'menu',
    'aria-controls': dropdownListId,
    'aria-expanded': !!isActive
  })), /*#__PURE__*/React__default__default.createElement(SubMenuContext.Provider, {
    value: subMenuContextValue
  }, /*#__PURE__*/React__default__default.createElement(SubMenu, {
    id: dropdownListId,
    className: bemDropdown.e('items'),
    onMouseLeave: function (event) {
      return openOnHover && closeDropdown(event);
    },
    ref: menuRef
  }, links.map(function (_a, i) {
    var text = _a.text,
      href = _a.href,
      key = _a.key,
      activeItem = _a.activeItem,
      InlineIcon = _a.InlineIcon,
      RouterLink = _a.RouterLink,
      linkRest = __rest(_a, ["text", "href", "key", "activeItem", "InlineIcon", "RouterLink"]);
    return /*#__PURE__*/React__default__default.createElement(DropdownItem, {
      RouterLink: RouterLink,
      href: href,
      active: activeItem,
      linkRest: __assign({}, linkRest),
      InlineIcon: InlineIcon,
      key: key || i
    }, text);
  }))));
};
Dropdown.displayName = 'Dropdown';
Dropdown.propTypes = {
  active: PropTypes__default.bool,
  children: PropTypes__default.node.isRequired,
  extraClasses: PropTypes__default.string,
  links: PropTypes__default.arrayOf(PropTypes__default.shape({
    text: PropTypes__default.node,
    href: PropTypes__default.string,
    key: PropTypes__default.string,
    InlineIcon: InlineIconPropType,
    RouterLink: PropTypes__default.oneOfType([PropTypes__default.func, PropTypes__default.object])
  })).isRequired,
  onToggle: PropTypes__default.func,
  xPosition: PropTypes__default.oneOf(DROPDOWN_XPOS),
  yPosition: PropTypes__default.oneOf(DROPDOWN_YPOS),
  openOnHover: PropTypes__default.bool,
  smartPosition: PropTypes__default.bool
};
var dropdown = Injecss(Dropdown, styles);

export { Dropdown, dropdown as default };
